import { handleError } from '@risksmart-app/components/src/utils/errorUtils';
import type { AppRouter } from '@risksmart-app/trpc/src/routers/router';
import { QueryCache, QueryClient } from '@tanstack/react-query';
import { createTRPCContext } from '@trpc/tanstack-react-query';

import { isUnmountedTrpcQueryFetchError } from './trpcUtils';

let browserQueryClient: QueryClient | undefined;

const makeQueryClient = () => {
  return new QueryClient({
    // tanstack-query swallows query errors unless a consumer reads isError —
    // mirror the Apollo errorLink so silent failures still reach Sentry. The
    // portal previously had no reporter at all, so EVERY tRPC query failure
    // (taxonomy, response list, response detail) was invisible where the
    // Apollo branch logged it. Mirrors web's reporter (RSP-6539) —
    // packages/web/src/utils/trpc.ts — including its exclusions: mutations are
    // omitted (the form owns mutation error UX) and only the procedure path is
    // captured, never the full query key, which embeds procedure input.
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (isUnmountedTrpcQueryFetchError(error, query)) {
          return;
        }

        handleError(error, {
          tags: { source: 'trpc-query' },
          extra: { procedurePath: query.queryKey[0] },
        });
      },
    }),
    defaultOptions: {
      queries: {
        // Same stale-while-revalidate defaults web ships (COI-451) — see
        // packages/web/src/utils/trpc.ts. `staleTime: 0` keeps every mount
        // revalidating (so this is not the Apollo `no-cache` policy's
        // staleness behaviour being relaxed), while `gcTime > 0` lets a
        // remount render the previous response instantly instead of a blank
        // spinner. The earlier `gcTime: 0` here was a straight `no-cache`
        // emulation, i.e. the half of COI-451 web deliberately reversed.
        // `queryClient.clear()` on auth change (TrpcProvider) is what keeps
        // cached data from crossing session boundaries.
        gcTime: 5 * 60 * 1000, // Keep unused query data for 5 minutes
        staleTime: 0, // Always consider data stale
        refetchOnMount: true, // Always refetch when component mounts
        refetchOnWindowFocus: false, // Don't refetch on window focus by default
      },
    },
  });
};

export const getQueryClient = () => {
  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient();
  }

  return browserQueryClient;
};

export const { TRPCProvider, useTRPC, useTRPCClient } =
  createTRPCContext<AppRouter>();
